/* https://github.com/denislins/scrollmenu/, MIT License. Thanks! */

export default class ScrollMenu {
  constructor(selectors, options) {
    this.initDefaultOptions()
    this.extendOptions(options)
    this.initMenuItems(selectors)
    this.getSectionsPositions()
    this.bindWindowEvents()
    this.bindMenuItems()
  }

  initDefaultOptions() {
    this.options = {
      duration: 1200,
      activeOffset: 120,
      scrollOffset: 120,

      // http://gsgd.co.uk/sandbox/jquery/easing/jquery.easing.1.3.js
      easingFunction(t, b, c, d) {
        if ((t /= d / 2) < 1) return (c / 2) * t * t * t + b
        return (-c / 2) * (Math.cos((Math.PI * t) / d) - 1) + b
      },
    }
  }

  extendOptions(options) {
    if (!options) return

    for (const i in this.options) {
      if (Object.hasOwn(this.options, i) && options[i]) {
        this.options[i] = options[i]
      }
    }
  }

  initMenuItems(selectors) {
    this.items = []

    if (Array.isArray(selectors) === false) {
      selectors = [selectors]
    }

    selectors.forEach((selector) => {
      const elements = [].slice.call(document.querySelectorAll(selector))

      // Check if the target exists on this page
      // otherwise redirect it to the root page
      elements.forEach((element) => {
        const target = this.getTarget(element)

        if (target !== null) {
          this.items.push(element)
        }
      })
    })
  }

  getTargetOffset(item) {
    const target = this.getTarget(item)

    if (target === 0 || target === null) {
      return 0
    }
    return target.offsetTop
  }

  getTarget(item) {
    let target = null
    const selector = this.getTargetSelector(item)

    if (selector === null) {
      return null
    }

    target = document.querySelector(selector)

    return target
  }

  getTargetSelector(item) {
    const href = item.getAttribute("href")
    const selector = href.match(/#[a-z][\w\d-]*\b/gi)

    if (selector === null) {
      return null
    }

    return selector[0]
  }

  bindWindowEvents() {
    window.addEventListener("scroll", this.onWindowUpdate.bind(this), false)
    window.addEventListener("resize", this.onWindowUpdate.bind(this), false)
  }

  bindMenuItems() {
    this.items.forEach((item) => {
      item.addEventListener("click", this.onMenuItemClick.bind(this), false)
    })
  }

  onWindowUpdate() {
    this.getSectionsPositions()
    this.updateActiveMenuItem()
  }

  getSectionsPositions() {
    this.positions = this.items.map((item) => {
      return this.getTargetOffset(item) - this.options.activeOffset
    })
  }

  updateActiveMenuItem() {
    // if scrolled to the end of the page
    if (
      this.getScrollOffset() + window.innerHeight ===
      document.body.clientHeight
    ) {
      this.changeActiveMenuItem(this.items[this.items.length - 1])
    } else {
      const filtered = this.items.filter((_item, index) => {
        return this.positions[index] <= this.getScrollOffset()
      })

      if (filtered.length > 0) {
        this.changeActiveMenuItem(filtered[filtered.length - 1])
      } else {
        this.resetActiveMenuItem()
      }
    }
  }

  getScrollOffset() {
    return document.body.scrollTop || window.pageYOffset
  }

  resetActiveMenuItem() {
    this.items.forEach((item) => {
      item.classList.remove("active")
    })
  }

  changeActiveMenuItem(item) {
    if (!item.classList.contains("active")) {
      this.resetActiveMenuItem()
      item.classList.add("active")
    }
  }

  onMenuItemClick(event) {
    event.preventDefault()

    const index = this.items.indexOf(event.target)
    if (index === -1) return

    this.updateLocationHash(index)
    this.animatePageScroll(index)
  }

  updateLocationHash(index) {
    const selector = this.getTargetSelector(this.items[index])
    let newUrl = location.pathname + location.search

    if (selector !== null) {
      newUrl += selector
    }

    history.pushState(selector, document.title, newUrl)
  }

  animatePageScroll(index) {
    const totalOffset = -this.options.activeOffset + this.options.scrollOffset
    const newPosition = this.positions[index] - totalOffset
    this.scrollTo(newPosition, this.options.duration)
  }

  scrollTo(to, duration, increment) {
    if (duration <= 0) return

    const difference = to - this.getScrollOffset()
    increment = (increment || 0) + 10

    setTimeout(() => {
      const newOffset = this.options.easingFunction(
        increment,
        this.getScrollOffset(),
        difference,
        duration,
      )

      window.scroll(0, newOffset)

      if (duration === increment) {
        this.updateActiveMenuItem()
      } else {
        this.scrollTo(to, duration, increment)
      }
    }, 10)
  }
}
